// frontend/src/components/ui/LoadingState.tsx
// ───────────────────────────────────────────────────────────────────
// États de CHARGEMENT standardisés (SAUX-03). Remplacent les « Chargement… »
// en texte brut.
//
//   <LoadingState>   → bloc centré, réutilise <Spinner> existant (pas de
//                      nouveau spinner : mutualisation).
//   <TableSkeleton>  → lignes grises animées pour une table (perçu plus rapide
//                      qu'un spinner sur une liste). `prefers-reduced-motion`
//                      respecté via `motion-reduce:animate-none`.
//
// Non branchés ici — câblage en SAUX-11 / SAUX-21.
// ───────────────────────────────────────────────────────────────────

import { Spinner } from '@/components/ui/Spinner';

import type { JSX } from 'react';

export interface LoadingStateProps {
  /** Message annoncé aux lecteurs d'écran (et affiché sous le spinner). */
  label?: string;
}

export function LoadingState({ label = 'Chargement…' }: LoadingStateProps): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-10">
      <Spinner label={label} />
      <p className="mt-2 text-sm text-slate-500">{label}</p>
    </div>
  );
}

export interface TableSkeletonProps {
  /** Nombre de lignes fantômes (défaut 5). */
  rows?: number;
  /** Nombre de colonnes fantômes (défaut 4). */
  columns?: number;
  label?: string;
}

export function TableSkeleton({
  rows = 5,
  columns = 4,
  label = 'Chargement de la liste…',
}: TableSkeletonProps): JSX.Element {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="divide-y divide-slate-100">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={`row-${String(rowIndex)}`}
          data-testid="skeleton-row"
          aria-hidden="true"
          className="flex items-center gap-4 px-4 py-3"
        >
          {Array.from({ length: columns }, (_, colIndex) => (
            <span
              key={`cell-${String(colIndex)}`}
              className={`h-3 animate-pulse rounded bg-slate-200 motion-reduce:animate-none ${
                colIndex === 0 ? 'flex-[2]' : 'flex-1'
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
